import { useState, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';
import { BookText } from 'lucide-react';

/**
 * Wraps any text region. When the user selects a single word inside it,
 * shows a small floating "Look up" pill anchored above the selection.
 * Clicking the pill resolves the word and navigates to its dictionary entry.
 *
 * Mirrors the chapter reader's "Look up word" toolbar action but with a
 * single-purpose, lighter UI suitable for context windows / short prose.
 *
 * Props:
 *   children:           the text region
 *   resolvePath:        GET endpoint that returns { id, lemma } | { id: null }
 *                       Default: /teacher/dictionary/resolve
 *   showPath:           function(id) → URL for the dictionary entry
 *                       Default: id => `/teacher/dictionary/${id}`
 */
export default function WordLookupSelection({
  children,
  resolvePath = '/teacher/dictionary/resolve',
  showPath = (id) => `/teacher/dictionary/${id}`,
}) {
  const ref = useRef(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMouseUp = () => {
      setTimeout(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) return;
        const raw = sel.toString();
        const trimmed = raw.trim();
        if (!trimmed) return;
        // Single-word only.
        if (trimmed.split(/\s+/).length !== 1) return;

        const range = sel.getRangeAt(0);
        if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return;

        const word = trimmed.replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, '');
        if (!word) return;

        const rect = range.getBoundingClientRect();
        setPending({
          rect: { left: rect.left, top: rect.top, width: rect.width },
          word,
        });
        setError(null);
      }, 50);
    };
    el.addEventListener('mouseup', onMouseUp);
    return () => el.removeEventListener('mouseup', onMouseUp);
  }, []);

  // Dismiss on outside click / Escape.
  useEffect(() => {
    if (!pending) return;
    const onClick = (e) => {
      if (e.target.closest('[data-word-lookup-pill]')) return;
      setPending(null);
      window.getSelection()?.removeAllRanges();
    };
    const onKey = (e) => { if (e.key === 'Escape') setPending(null); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pending]);

  async function lookup() {
    if (!pending) return;
    try {
      const res = await fetch(`${resolvePath}?q=${encodeURIComponent(pending.word)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.id) {
          router.visit(showPath(data.id));
          return;
        }
      }
      setError(`No dictionary entry for "${pending.word}"`);
    } catch {
      setError('Lookup failed — network error');
    }
  }

  return (
    <div ref={ref} className="relative">
      {children}
      {pending && (
        <button
          data-word-lookup-pill
          onClick={lookup}
          onMouseDown={(e) => e.stopPropagation()}
          className="fixed z-50 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          style={{
            left: `${pending.rect.left + pending.rect.width / 2}px`,
            top: `${pending.rect.top - 8}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <BookText className="w-3 h-3" strokeWidth={1.5} />
          Look up &ldquo;{pending.word}&rdquo;
        </button>
      )}
      {error && (
        <p className="mt-2 text-sm text-rose-500">{error}</p>
      )}
    </div>
  );
}
